import React, { useMemo } from "react"
import useDimensions from "react-use-dimensions"
import DataLoader, { years } from "../DataLoader"
import { extent } from "d3-array"
import { scaleLinear } from "d3-scale"
import gradient from "../utils/gradient"
import "./styles.scss"

export default function HistoricalBubbleGraph(props) {
  const { selectedCountry, scoreKey, smallVersion = false } = props // Added smallVersion prop

  const [ref, containerDimensions] = useDimensions()
  const margins = useMemo(
    () => ({ left: 20, right: 20, top: 20, bottom: 44 }),
    [],
  )

  const containerWidth = useMemo(
    () => Math.min(Math.max(170, ~~containerDimensions.width), 600), //change from 600 to 460 for pdf generation
    [containerDimensions.width],
  )

  // Adjust the height based on the smallVersion prop
  const containerHeight = useMemo(
    () =>
      smallVersion
        ? Math.max(~~containerDimensions.height, 120)
        : Math.max(~~containerDimensions.height, 200), // Half height for small version
    [containerDimensions.height, smallVersion],
  )

  const width = useMemo(
    () => containerWidth - margins.left - margins.right,
    [containerWidth, margins],
  )

  const height = useMemo(
    () => containerHeight - margins.top - margins.bottom - 6,
    [containerHeight, margins],
  )

  const historicalData = useMemo(() => {
    return years
      .map((year) => {
        const countryDatum = DataLoader.dataByYear[year].countries.find(
          (country) => country.Country === selectedCountry,
        )
        return {
          year,
          data: countryDatum,
        }
      })
      .filter((datum) => datum.data)
  }, [selectedCountry])

  const xScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, historicalData.length - 1])
        .range([0, width]),
    [historicalData.length, width],
  )

  const dataExtent = useMemo(
    () => extent(historicalData, (d) => d.data[scoreKey]),
    [historicalData, scoreKey],
  )

  const yScale = useMemo(
    () => scaleLinear().domain(dataExtent).range([height, 0]),
    [dataExtent, height],
  )

  const gridlines = useMemo(() => {
    if (smallVersion) return null // Skip rendering gridlines if smallVersion is true
    const numberOfGridlines = 4
    return Array.from({ length: numberOfGridlines }).map((_, index) => {
      const yValue = (index / (numberOfGridlines - 1)) * height
      return (
        <line
          key={index}
          x1={0}
          x2={width}
          y1={height - yValue}
          y2={height - yValue}
          stroke="lightgrey"
          strokeDasharray="4"
        />
      )
    })
  }, [height, width, smallVersion])

  const yearDots = useMemo(() => {
    return historicalData.map((datum, index) => {
      const score = datum.data[scoreKey]
      const x = xScale(index)
      const yClamped = Math.min(Math.max(yScale(score), 6), height - 6)
      const fill = gradient(score)
      const label = datum.year

      const yearLabel =
        label === "2017-18" ? (
          <g
            transform={`translate(0, ${height + 28 + 6})`}
            fill={props.fontColor}
          >
            <text dy={-14}>'17</text>
            <text dy={0}>'18</text>
          </g>
        ) : (
          <text y={height + 28 + 6} fill={props.fontColor}>
            {"'" + label.substr(2)}
          </text>
        )

      const valueLabel = score.toFixed(2).replace(/^0\./, ".")

      return (
        <g key={index} transform={`translate(${x},0)`}>
          <g transform={`translate(0, ${yClamped})`}>
            <text y={-10} fill={props.fontColor} fontWeight="700">
              {valueLabel}
            </text>
            <circle fill={fill} r={6} stroke={props.stroke} />
          </g>
          {yearLabel}
        </g>
      )
    })
  }, [
    historicalData,
    scoreKey,
    xScale,
    yScale,
    height,
    props.fontColor,
    props.stroke,
  ])

  const polylinePoints = useMemo(() => {
    return historicalData
      .map((datum, index) => {
        const score = datum.data[scoreKey]
        const x = xScale(index)
        const yClamped = Math.min(Math.max(yScale(score), 6), height - 6)
        return [x, yClamped].join(",")
      })
      .join(" ")
  }, [historicalData, scoreKey, xScale, yScale, height])

  return (
    <div className="historicalBubbleGraph" ref={ref}>
      <svg width={containerWidth} height={containerHeight}>
        <g transform={`translate(${margins.left}, ${margins.top + 6 / 2})`}>
          {!smallVersion && gridlines} {/* Conditionally render gridlines */}
          <polyline points={polylinePoints} stroke={props.stroke} fill="none" />
          {yearDots}
        </g>
      </svg>
    </div>
  )
}

HistoricalBubbleGraph.defaultProps = {
  stroke: "#56565A",
  fontColor: "#56565A",
  smallVersion: false, // Default to the larger version
}
